import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LEADQ } from "@/constants/testIds";
import {
  SERVICES, PRIORITIES, MEMBERSHIP, SG_OPS, STATUS_LABELS, STATUS_STYLE, labelOf, fmtSgt,
} from "@/constants/campaignDefs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ShieldAlert, Users, ArrowLeft, Mail, Phone, MessageCircle, Clock, Sprout, Download, BarChart3, Link2 } from "lucide-react";
import { toast } from "sonner";

const selCls = "h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm text-slate-700 focus:border-forest-mid focus:outline-none";

function Chip({ children, className = "" }) {
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${className}`}>{children}</span>;
}
const CHAN_ICON = { email: Mail, phone: Phone, whatsapp: MessageCircle };
const FUNNEL_LABELS = { landing_view: "Landing views", cta_click: "CTA clicks", form_start: "Form starts", register_success: "Registrations", profile_saved: "Profiles saved", profile_skipped: "Profiles skipped" };

function LeadDetail({ id, onClose, onChanged }) {
  const [lead, setLead] = useState(null);
  const [status, setStatus] = useState("");
  const [reason, setReason] = useState("");
  const [owner, setOwner] = useState("");
  const [uen, setUen] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/campaign/leads/${id}`);
      setLead(r.data); setStatus(r.data.status); setOwner(r.data.owner || ""); setUen(r.data.uen || "");
    } catch (e) { toast.error(apiErr(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const applyStatus = async () => {
    setBusy(true);
    try {
      await api.post(`/campaign/leads/${id}/status`, { status, reason: reason || undefined });
      toast.success(`Lead moved to ${STATUS_LABELS[status]}`);
      await load(); onChanged?.();
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };
  const release = async () => {
    setBusy(true);
    try { await api.post(`/campaign/leads/${id}/release`); toast.success("Place released"); await load(); onChanged?.(); }
    catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };
  const saveOwner = async () => {
    try { await api.patch(`/campaign/leads/${id}`, { owner }); toast.success("Owner updated"); await load(); onChanged?.(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const saveUen = async () => {
    try { await api.patch(`/campaign/leads/${id}`, { uen }); toast.success("UEN updated"); await load(); onChanged?.(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!lead) return null;
  const consent = lead.consent || {};
  const Icon = CHAN_ICON[lead.contact_channel] || Mail;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid={LEADQ.detail}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-base">
            {lead.company} <Chip className={STATUS_STYLE[lead.status]}>{STATUS_LABELS[lead.status]}</Chip>
            {lead.is_place_confirmed && <Chip className="border-[#9ccc2f] bg-[#B7E64B] text-[#09292D]">Place held</Chip>}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="rounded-lg border border-slate-200 p-3">
            <p className="font-semibold text-forest-dark">{lead.contact_name} · <span className="font-mono text-xs text-muted-foreground">{lead.reference}</span></p>
            <p className="mt-1 flex items-center gap-1.5 text-muted-foreground"><Icon className="h-3.5 w-3.5" /> {lead.contact_email}{lead.phone ? ` · ${lead.phone}` : ""} <span className="text-xs">({lead.contact_channel})</span></p>
            <p className="mt-1 text-xs text-muted-foreground">Registered {fmtSgt(lead.created_at)}{lead.waitlist ? " · waitlisted" : ""}</p>
            <p className="mt-0.5 flex flex-wrap items-center gap-1 text-xs">
              {lead.metrics?.overdue && <span className="rounded bg-rose-100 px-1.5 py-0.5 font-semibold text-rose-700">Overdue</span>}
              <span className="text-muted-foreground">{lead.metrics?.response_hours != null ? `First response ${lead.metrics.response_hours}h` : "Awaiting first response"}{lead.metrics?.stage_hours != null ? ` · ${lead.metrics.stage_hours}h in stage` : ""}</span>
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs">
            <div><span className="text-muted-foreground">LIAS membership:</span> <b>{labelOf(MEMBERSHIP, lead.lias_membership)}</b></div>
            <div><span className="text-muted-foreground">Singapore ops:</span> <b>{labelOf(SG_OPS, lead.sg_operations)}</b></div>
            <div className="col-span-2"><span className="text-muted-foreground">Services:</span> {lead.services.map((s) => labelOf(SERVICES, s)).join(", ")}</div>
            <div className="col-span-2"><span className="text-muted-foreground">Priorities:</span> {lead.priorities.map((s) => labelOf(PRIORITIES, s)).join(", ")}</div>
          </div>

          {lead.profile && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs">
              <p className="mb-1 font-semibold text-forest-dark">Optional profile</p>
              {Object.entries(lead.profile).filter(([, v]) => v && (!Array.isArray(v) || v.length)).map(([k, v]) => (
                <p key={k}><span className="text-muted-foreground">{k.replace(/_/g, " ")}:</span> {Array.isArray(v) ? v.join(", ") : String(v)}</p>
              ))}
            </div>
          )}

          {/* Owner */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-muted-foreground">Owner</span>
            <input value={owner} onChange={(e) => setOwner(e.target.value)} data-testid={LEADQ.ownerInput} className={`${selCls} flex-1`} />
            <Button size="sm" variant="outline" onClick={saveOwner} data-testid={LEADQ.ownerSave}>Save</Button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-muted-foreground">UEN</span>
            <input value={uen} onChange={(e) => setUen(e.target.value)} data-testid={LEADQ.uenInput} className={`${selCls} flex-1`} placeholder="e.g. 201812345A" />
            <Button size="sm" variant="outline" onClick={saveUen} data-testid={LEADQ.uenSave}>Save</Button>
          </div>
          {lead.related?.length > 0 && (
            <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-xs" data-testid={LEADQ.related}>
              <p className="mb-1 flex items-center gap-1 font-semibold text-indigo-900"><Link2 className="h-3.5 w-3.5" /> Related enquiries (same UEN)</p>
              {lead.related.map((rl) => (
                <p key={rl.id}>{rl.company} · {rl.contact_name} · <span className="font-mono">{rl.reference}</span> · {STATUS_LABELS[rl.status]}</p>
              ))}
            </div>
          )}

          {/* Status control */}
          <div className="rounded-lg border border-slate-200 p-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Next action</p>
            <div className="flex flex-wrap items-center gap-2">
              <select value={status} onChange={(e) => setStatus(e.target.value)} className={selCls} data-testid={LEADQ.statusSelect}>
                {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <Button size="sm" onClick={applyStatus} disabled={busy} className="bg-forest-mid hover:bg-forest-dark" data-testid={LEADQ.statusApply}>Apply</Button>
              {lead.is_place_confirmed && <Button size="sm" variant="outline" onClick={release} disabled={busy} data-testid={LEADQ.releaseBtn}>Release place</Button>}
            </div>
            {status === "not_proceeding" && (
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid={LEADQ.statusReason} rows={2}
                        placeholder="Reason (required)" className="mt-2 w-full rounded-lg border border-slate-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-forest-mid" />
            )}
          </div>

          {/* Consent history */}
          <div className="rounded-lg border border-slate-200 p-3 text-xs">
            <p className="mb-1 font-semibold text-forest-dark">Consent</p>
            <p><span className="text-muted-foreground">Notice version:</span> {consent.notice_version} · submitted {fmtSgt(consent.submitted_at)}</p>
            <p><span className="text-muted-foreground">Marketing:</span> {consent.marketing_opt_in ? `Opted in ${fmtSgt(consent.marketing_opt_in_at)}` : "Not opted in"}{consent.unsubscribed_at ? ` · unsubscribed ${fmtSgt(consent.unsubscribed_at)}` : ""}</p>
          </div>

          {/* Activity */}
          <div className="rounded-lg border border-slate-200 p-3 text-xs">
            <p className="mb-1 font-semibold text-forest-dark">Activity</p>
            <div className="space-y-1">
              {(lead.activity || []).slice().reverse().map((a, i) => (
                <p key={i} className="flex items-start gap-1.5"><Clock className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" /><span><b>{a.action.replace(/_/g, " ")}</b> — {a.detail} <span className="text-muted-foreground">({a.actor}, {fmtSgt(a.at)})</span></span></p>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function LeadQueuePage() {
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ membership: "", sg: "", priority: "", service: "", owner: "", status: "", age: "" });
  const [openId, setOpenId] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  const load = useCallback(async () => {
    const qs = Object.entries(filters).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
    try {
      const r = await api.get(`/campaign/leads${qs ? `?${qs}` : ""}`);
      setData(r.data);
    } catch (e) {
      if (e?.response?.status !== 403) toast.error(apiErr(e));
    }
  }, [filters]);

  useEffect(() => { if (hasRole("tenant_admin")) load(); }, [load, hasRole]);
  useEffect(() => {
    if (hasRole("tenant_admin")) api.get("/campaign/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  }, [hasRole, openId]);

  const exportCsv = () => downloadFile("/campaign/leads/export.csv", "liasroots-leads.csv").catch(() => toast.error("Export failed"));

  const changeState = async (state) => {
    try { await api.put("/campaign/config", { state }); toast.success(`Campaign set to ${state}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (user && !hasRole("tenant_admin")) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas" data-testid={LEADQ.root}>
        <Card className="max-w-sm p-8 text-center">
          <ShieldAlert className="mx-auto h-8 w-8 text-rose-500" />
          <p className="mt-3 font-heading text-lg font-semibold text-forest-dark">Not authorised</p>
          <p className="mt-1 text-sm text-muted-foreground">The LIASROOTS lead queue is for campaign administrators.</p>
          <Button className="mt-4" variant="outline" onClick={() => navigate("/")}>Back to ROOTS</Button>
        </Card>
      </div>
    );
  }

  const c = data?.counts;
  const statTiles = [
    ["registrations", "Registrations"], ["potentially_eligible", "Potentially eligible"],
    ["verified", "Verified orgs"], ["scope_proposals", "Scope proposals"],
    ["confirmed", "Confirmed"], ["waitlist", "Waitlist"],
  ];

  return (
    <div className="min-h-screen bg-canvas" data-testid={LEADQ.root}>
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between bg-[#09292D] px-5 text-white">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#B7E64B]"><Users className="h-4 w-4 text-[#09292D]" /></div>
          <div className="leading-tight">
            <p className="font-heading text-base font-bold">LIASROOTS · Lead Queue</p>
            <p className="-mt-0.5 text-[10px] uppercase tracking-widest text-white/50">Campaign operations</p>
          </div>
        </div>
        <Button size="sm" variant="secondary" className="bg-white/10 text-white hover:bg-white/20" onClick={() => navigate("/")}><ArrowLeft className="mr-1 h-4 w-4" /> ROOTS</Button>
      </header>

      <div className="mx-auto max-w-[1160px] px-5 py-6">
        {/* Stat tiles */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {statTiles.map(([k, label]) => (
            <Card key={k} className="p-3" data-testid={LEADQ.stat(k)}>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
              <p className="mt-0.5 font-heading text-2xl font-bold text-forest-dark">{c ? c[k] : "—"}</p>
            </Card>
          ))}
        </div>

        {/* Capacity + campaign state */}
        {data && (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#9ccc2f] bg-[#f3f8e6] p-3">
            <p className="flex items-center gap-2 text-sm font-semibold text-[#09292D]" data-testid={LEADQ.capacity}>
              <Sprout className="h-4 w-4" /> Pilot places: <b>{data.confirmed_count}/{data.capacity}</b> filled · {data.places_remaining} remaining
            </p>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-[#09292D]">Campaign state</span>
              <select value={data.state} onChange={(e) => changeState(e.target.value)} className={selCls} data-testid={LEADQ.stateSelect}>
                <option value="open">Open</option><option value="full">Full / waitlist</option><option value="closed">Closed</option>
              </select>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="mt-4 flex flex-wrap gap-2">
          <select className={selCls} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid={LEADQ.filter("status")}>
            <option value="">All statuses</option>{Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select className={selCls} value={filters.membership} onChange={(e) => setFilters({ ...filters, membership: e.target.value })} data-testid={LEADQ.filter("membership")}>
            <option value="">Any membership</option>{MEMBERSHIP.map((m) => <option key={m.key} value={m.key}>LIAS: {m.label}</option>)}
          </select>
          <select className={selCls} value={filters.sg} onChange={(e) => setFilters({ ...filters, sg: e.target.value })} data-testid={LEADQ.filter("sg")}>
            <option value="">Any SG ops</option>{SG_OPS.map((m) => <option key={m.key} value={m.key}>SG: {m.label}</option>)}
          </select>
          <select className={selCls} value={filters.service} onChange={(e) => setFilters({ ...filters, service: e.target.value })} data-testid={LEADQ.filter("service")}>
            <option value="">Any service</option>{SERVICES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <select className={selCls} value={filters.priority} onChange={(e) => setFilters({ ...filters, priority: e.target.value })} data-testid={LEADQ.filter("priority")}>
            <option value="">Any priority</option>{PRIORITIES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <select className={selCls} value={filters.owner} onChange={(e) => setFilters({ ...filters, owner: e.target.value })} data-testid={LEADQ.filter("owner")}>
            <option value="">Any owner</option>{(data?.owners || []).map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <select className={selCls} value={filters.age} onChange={(e) => setFilters({ ...filters, age: e.target.value })} data-testid={LEADQ.filter("age")}>
            <option value="">Any age</option><option value="7">Last 7 days</option><option value="30">Last 30 days</option><option value="90">Last 90 days</option>
          </select>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            {data ? `${data.leads.length} shown` : ""}
            {data?.avg_response_hours != null ? ` · avg first response ${data.avg_response_hours}h` : ""}
            {c?.overdue ? " · " : ""}{c?.overdue ? <span className="font-semibold text-rose-600">{c.overdue} overdue (SLA {data?.sla_hours}h)</span> : null}
          </p>
          <Button size="sm" variant="outline" onClick={exportCsv} data-testid={LEADQ.exportBtn}><Download className="mr-1 h-4 w-4" /> Export CSV</Button>
        </div>

        {/* Table */}
        <Card className="mt-4 p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead><TableHead>Contact</TableHead><TableHead className="hidden sm:table-cell">LIAS</TableHead>
                  <TableHead className="hidden md:table-cell">Owner</TableHead><TableHead>Status</TableHead><TableHead className="hidden lg:table-cell">Registered</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.leads || []).map((l) => (
                  <TableRow key={l.id} data-testid={LEADQ.row(l.id)} className="cursor-pointer hover:bg-sage-surface/50" onClick={() => setOpenId(l.id)}>
                    <TableCell>
                      <p className="font-medium text-forest-dark">{l.company}</p>
                      <p className="font-mono text-[11px] text-muted-foreground">{l.reference}</p>
                    </TableCell>
                    <TableCell className="text-sm">{l.contact_name}<span className="block text-[11px] text-muted-foreground">{l.contact_channel}</span></TableCell>
                    <TableCell className="hidden sm:table-cell text-sm">{labelOf(MEMBERSHIP, l.lias_membership)}</TableCell>
                    <TableCell className="hidden md:table-cell text-sm">{l.owner}</TableCell>
                    <TableCell><Chip className={STATUS_STYLE[l.status]}>{STATUS_LABELS[l.status]}</Chip>{l.waitlist && l.status !== "waitlisted" && <span className="ml-1 text-[10px] text-purple-600">wl</span>}{l.overdue && <span className="ml-1 text-[10px] font-semibold text-rose-600">overdue</span>}</TableCell>
                    <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">{fmtSgt(l.created_at)}</TableCell>
                  </TableRow>
                ))}
                {data && data.leads.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">No leads match these filters.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </Card>

        {analytics && (
          <Card className="mt-4 p-4" data-testid={LEADQ.analytics}>
            <p className="flex items-center gap-2 font-heading text-sm font-bold text-forest-dark"><BarChart3 className="h-4 w-4 text-forest-mid" /> Campaign funnel <span className="text-[11px] font-normal text-muted-foreground">· anonymous, no personal data</span></p>
            <div className="mt-3 space-y-1.5">
              {analytics.funnel.map((fn) => {
                const max = analytics.funnel[0].count || 1;
                return (
                  <div key={fn.event} className="flex items-center gap-2">
                    <span className="w-32 shrink-0 text-xs text-muted-foreground">{FUNNEL_LABELS[fn.event]}</span>
                    <div className="h-4 flex-1 rounded bg-slate-100"><div className="h-4 rounded bg-forest-mid" style={{ width: `${Math.min(100, (fn.count / max) * 100)}%` }} /></div>
                    <span className="w-10 text-right text-xs font-semibold text-forest-dark">{fn.count}</span>
                  </div>
                );
              })}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">Conversion (landing → registration): <b>{analytics.conversion_rate == null ? "—" : `${analytics.conversion_rate}%`}</b> · {analytics.unique_sessions} sessions · {analytics.total_events} events</p>
          </Card>
        )}
      </div>

      {openId && <LeadDetail id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}
